import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, ChevronRight, ChevronLeft, Calendar, MapPin, Tag, Mail, Users, Globe, Lock, Plus, Trash2, Info, AlertTriangle, CreditCard, ExternalLink, Loader2, CheckCircle2, Link2 } from 'lucide-react';
import { Label } from '../../ui/label';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { CurrencyInput } from './FormComponents/CurrencyInput';
import { FileInput } from './FormComponents/FileInput';
import { LocationPicker } from '../../LocationPicker';

import { supabase } from '../../../lib/supabase';
import { searchAddress } from '../../../lib/utils/geocoding';
import { createConnectAccount, getConnectStatus } from '../../../lib/stripe/connect';
import type { Community } from '../../../types/communities';
import type { Vibe, SubCategory } from '../../../types/vibes';
import { VIBE_COLORS, DEFAULT_VIBES } from '../../../types/vibes';

interface EventFormProps {
  onClose: () => void;
  onSuccess: () => void;
  editId?: string;
}

type Step = 'basics' | 'details' | 'tickets' | 'media' | 'community';

interface StepConfig {
  id: Step;
  title: string;
  icon: React.ComponentType<any>;
}

const steps: StepConfig[] = [
  { id: 'basics', title: 'Basic Info', icon: Calendar },
  { id: 'details', title: 'Details', icon: MapPin },
  { id: 'tickets', title: 'Tickets', icon: Tag },
  { id: 'media', title: 'Media', icon: Mail },
  { id: 'community', title: 'Community', icon: Users },
];

export function EventForm({ onClose, onSuccess, editId }: EventFormProps) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState<Step>('basics');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    location: '',
    country: '',
    latitude: null as number | null,
    longitude: null as number | null,
    price: '',
    ticket_url: '',
    community_id: '',
    visibility: 'public' as 'public' | 'private'
  });
  const [currency, setCurrency] = useState('GBP');
  const [images, setImages] = useState<File[]>([]);
  const [loading, setLoading] = useState(!!editId);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [enableTicketSales, setEnableTicketSales] = useState(false);
  const [freeEventCapacity, setFreeEventCapacity] = useState('');
  const [ticketSalesMode, setTicketSalesMode] = useState<'platform' | 'external'>('platform');
  const [externalTicketUrl, setExternalTicketUrl] = useState('');
  const [externalTicketPrice, setExternalTicketPrice] = useState('');
  const [ticketTiers, setTicketTiers] = useState<{ name: string; price: string; quantity: string; description: string }[]>([
    { name: 'Regular', price: '', quantity: '', description: '' }
  ]);
  const [salesStartDate, setSalesStartDate] = useState('');
  const [salesStartTime, setSalesStartTime] = useState('09:00');
  const [salesEndDate, setSalesEndDate] = useState('');
  const [salesEndTime, setSalesEndTime] = useState('23:59');
  const [vatPercentage, setVatPercentage] = useState('');
  const [organiserBusinessName, setOrganiserBusinessName] = useState('');
  const [organiserAddress, setOrganiserAddress] = useState('');
  const [organiserPhone, setOrganiserPhone] = useState('');
  const [organiserPhoneCode, setOrganiserPhoneCode] = useState('+44');
  const [organiserEmail, setOrganiserEmail] = useState('');
  const [organiserTaxNumber, setOrganiserTaxNumber] = useState('');
  const [organiserAgreedToTerms, setOrganiserAgreedToTerms] = useState(false);
  const [addressQuery, setAddressQuery] = useState('');
  const [addressResults, setAddressResults] = useState<Array<{ display_name: string; place_id: number }>>([]);
  const [showAddressResults, setShowAddressResults] = useState(false);
  const [addressSearching, setAddressSearching] = useState(false);
  const addressTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const addressResultsRef = React.useRef<HTMLDivElement>(null);
  const [communities, setCommunities] = useState<Community[]>([]);
  const [userCommunities, setUserCommunities] = useState<Community[]>([]);
  const [existingImageUrls, setExistingImageUrls] = useState<string[]>([]);
  const [stripeConnectStatus, setStripeConnectStatus] = useState<string | null>(null);
  const [stripeConnectLoading, setStripeConnectLoading] = useState(false);
  const [hasExistingTicketSales, setHasExistingTicketSales] = useState(false);

  // Vibe state
  const [vibes, setVibes] = useState<Vibe[]>([]);
  const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
  const [selectedVibe, setSelectedVibe] = useState<string>('');
  const [selectedSubCategory, setSelectedSubCategory] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [suitableForKids, setSuitableForKids] = useState(false);

  // Country phone codes
  const phoneCodes = [
    { code: '+44', label: '🇬🇧 +44', country: 'UK' },
    { code: '+1', label: '🇺🇸 +1', country: 'US' },
    { code: '+31', label: '🇳🇱 +31', country: 'NL' },
    { code: '+33', label: '🇫🇷 +33', country: 'FR' },
    { code: '+34', label: '🇪🇸 +34', country: 'ES' },
    { code: '+49', label: '🇩🇪 +49', country: 'DE' },
    { code: '+39', label: '🇮🇹 +39', country: 'IT' },
    { code: '+32', label: '🇧🇪 +32', country: 'BE' },
    { code: '+41', label: '🇨🇭 +41', country: 'CH' },
    { code: '+43', label: '🇦🇹 +43', country: 'AT' },
    { code: '+45', label: '🇩🇰 +45', country: 'DK' },
    { code: '+46', label: '🇸🇪 +46', country: 'SE' },
    { code: '+47', label: '🇳🇴 +47', country: 'NO' },
    { code: '+48', label: '🇵🇱 +48', country: 'PL' },
    { code: '+351', label: '🇵🇹 +351', country: 'PT' },
    { code: '+353', label: '🇮🇪 +353', country: 'IE' },
    { code: '+358', label: '🇫🇮 +358', country: 'FI' },
    { code: '+30', label: '🇬🇷 +30', country: 'GR' },
    { code: '+36', label: '🇭🇺 +36', country: 'HU' },
    { code: '+420', label: '🇨🇿 +420', country: 'CZ' },
    { code: '+61', label: '🇦🇺 +61', country: 'AU' },
    { code: '+64', label: '🇳🇿 +64', country: 'NZ' },
    { code: '+91', label: '🇮🇳 +91', country: 'IN' },
    { code: '+81', label: '🇯🇵 +81', country: 'JP' },
    { code: '+86', label: '🇨🇳 +86', country: 'CN' },
    { code: '+55', label: '🇧🇷 +55', country: 'BR' },
    { code: '+27', label: '🇿🇦 +27', country: 'ZA' },
    { code: '+971', label: '🇦🇪 +971', country: 'AE' },
    { code: '+966', label: '🇸🇦 +966', country: 'SA' },
    { code: '+90', label: '🇹🇷 +90', country: 'TR' },
  ];

  const handleAddressQueryChange = (value: string) => {
    setAddressQuery(value);
    setOrganiserAddress(value);
    if (addressTimeoutRef.current) clearTimeout(addressTimeoutRef.current);
    addressTimeoutRef.current = setTimeout(async () => {
      if (value.length < 3) {
        setAddressResults([]);
        setShowAddressResults(false);
        return;
      }
      setAddressSearching(true);
      try {
        const data = await searchAddress(value);
        setAddressResults(data);
        setShowAddressResults(data.length > 0);
      } catch {
        setAddressResults([]);
      } finally {
        setAddressSearching(false);
      }
    }, 500);
  };

  const selectAddressResult = (result: { display_name: string }) => {
    setOrganiserAddress(result.display_name);
    setAddressQuery('');
    setAddressResults([]);
    setShowAddressResults(false);
  };

  // Close address dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (addressResultsRef.current && !addressResultsRef.current.contains(e.target as Node)) {
        setShowAddressResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check Stripe Connect status when ticket sales is enabled
  useEffect(() => {
    if (enableTicketSales && stripeConnectStatus === null) {
      getConnectStatus().then((status) => {
        setStripeConnectStatus(status.status || 'none');
      }).catch(() => {
        setStripeConnectStatus('none');
      });
    }
  }, [enableTicketSales]);

  const handleSetupStripeConnect = async () => {
    try {
      setStripeConnectLoading(true);
      const result = await createConnectAccount();
      if (result.onboardingUrl) {
        window.open(result.onboardingUrl, '_blank');
      } else if (result.status === 'active') {
        setStripeConnectStatus('active');
      }
    } catch (err: any) {
      console.error('Error setting up Stripe Connect:', err);
    } finally {
      setStripeConnectLoading(false);
    }
  };

  useEffect(() => {
    const fetchUserCommunities = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Fetch communities where user is admin
        const { data: adminCommunities } = await supabase
          .from('community_members')
          .select(`
            community_id,
            communities:community_id (
              id,
              name,
              description,
              type,
              owner_id,
              avatar_url,
              banner_url,
              created_at,
              updated_at
            )
          `)
          .eq('user_id', user.id)
          .eq('role', 'admin');

        if (adminCommunities) {
          const communities = adminCommunities.map(member => member.communities).filter(Boolean);
          setUserCommunities(communities as Community[]);
        }
      } catch (error) {
        console.error('Error fetching user communities:', error);
      }
    };

    fetchUserCommunities();
  }, []);

  // Fetch vibes and sub-categories
  useEffect(() => {
    async function fetchVibes() {
      const { data: vibesData } = await supabase
        .from('vibes')
        .select('*')
        .order('display_order');

      const { data: subCatsData } = await supabase
        .from('sub_categories')
        .select('*')
        .order('display_order');

      if (vibesData) setVibes(vibesData);
      if (subCatsData) setSubCategories(subCatsData);
    }
    fetchVibes();
  }, []);

  useEffect(() => {
    if (editId) {
      const fetchEvent = async () => {
        setLoading(true);
        try {
          const { data, error } = await supabase
            .from('events')
            .select('*')
            .eq('id', editId)
            .single();

          if (error) throw error;
          if (!data) throw new Error('Event not found');

          const startDate = new Date(data.start_date);
          const endDate = new Date(data.end_date);
          
          const formatDateForInput = (date: Date) => {
            return date.toISOString().split('T')[0];
          };
          
          const formatTimeForInput = (date: Date) => {
            return date.toISOString().split('T')[1].substring(0, 5);
          };

          setFormData({
            title: data.title,
            description: data.description,
            start_date: formatDateForInput(startDate),
            start_time: formatTimeForInput(startDate),
            end_date: formatDateForInput(endDate),
            end_time: formatTimeForInput(endDate),
            location: data.location,
            country: data.country || '',
            latitude: data.latitude || null,
            longitude: data.longitude || null,
            price: data.price ? data.price.toString() : '',
            ticket_url: data.ticket_url || '',
            community_id: data.community_id || '',
            visibility: data.visibility || 'public'
          });

          // Load vibe data for editing
          if (data.primary_vibe) setSelectedVibe(data.primary_vibe);
          if (data.sub_category_id) setSelectedSubCategory(data.sub_category_id);
          if (data.vibe_tags && Array.isArray(data.vibe_tags)) setSelectedTags(data.vibe_tags);
          if (data.suitable_for_kids) setSuitableForKids(data.suitable_for_kids);

          const hasTicketTiers = data.ticket_tiers && Array.isArray(data.ticket_tiers) && data.ticket_tiers.length > 0;
          const hasExternalUrl = !!data.ticket_url;
          setEnableTicketSales(hasTicketTiers || hasExternalUrl || (!!data.price && data.price > 0));
          setHasExistingTicketSales(hasTicketTiers);
          // Restore the correct ticket sales mode
          if (hasExternalUrl && !hasTicketTiers) {
            setTicketSalesMode('external');
            setExternalTicketUrl(data.ticket_url || '');
            setExternalTicketPrice(data.price?.toString() || '');
          } else {
            setTicketSalesMode('platform');
          }
          setCurrency(data.currency || 'GBP');

          // Load max_participants for free events
          if (data.max_participants && !hasTicketTiers && !hasExternalUrl) {
            setFreeEventCapacity(data.max_participants.toString());
          }

          // Restore ticket tiers if available
          if (data.ticket_tiers && Array.isArray(data.ticket_tiers)) {
            setTicketTiers(data.ticket_tiers.map((t: any) => ({
              name: t.name || '',
              price: t.price?.toString() || '',
              quantity: t.quantity?.toString() || '',
              description: t.description || ''
            })));
          }
          if (data.sales_start_date) {
            const salesStart = new Date(data.sales_start_date);
            setSalesStartDate(salesStart.toISOString().split('T')[0]);
            setSalesStartTime(salesStart.toISOString().split('T')[1].substring(0, 5));
          }
          if (data.sales_end_date) {
            const salesEnd = new Date(data.sales_end_date);
            setSalesEndDate(salesEnd.toISOString().split('T')[0]);
            setSalesEndTime(salesEnd.toISOString().split('T')[1].substring(0, 5));
          }
          if (data.vat_percentage != null) {
            setVatPercentage(data.vat_percentage.toString());
          }
          if (data.organiser_business_name) setOrganiserBusinessName(data.organiser_business_name);
          if (data.organiser_address) setOrganiserAddress(data.organiser_address);
          if (data.organiser_phone) {
            // Parse stored phone (e.g. "+44612345678") back into code + number
            const storedPhone = data.organiser_phone as string;
            const matchedCode = phoneCodes
              .map(pc => pc.code)
              .sort((a, b) => b.length - a.length) // longest first for accurate match
              .find(code => storedPhone.startsWith(code));
            if (matchedCode) {
              setOrganiserPhoneCode(matchedCode);
              setOrganiserPhone(storedPhone.slice(matchedCode.length));
            } else {
              setOrganiserPhone(storedPhone);
            }
          }
          if (data.organiser_email) setOrganiserEmail(data.organiser_email);
          if (data.organiser_tax_number) setOrganiserTaxNumber(data.organiser_tax_number);
          // If editing an event with existing ticket sales, they already agreed to terms
          if (data.ticket_tiers && data.ticket_tiers.length > 0) {
            setOrganiserAgreedToTerms(true);
          }
          
          if (data.images && data.images.length > 0) {
            setExistingImageUrls(data.images);
          } else if (data.image_url) {
            setExistingImageUrls([data.image_url]);
          }
        } catch (err: any) {
          console.error('Error fetching event:', err);
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

      fetchEvent();
    }
  }, [editId]);

  const isStepValid = () => {
    switch (currentStep) {
      case 'basics':
        // Vibe is required - must select a vibe
        if (!selectedVibe) return false;
        return formData.title && formData.description;
      case 'details':
        return formData.start_date && formData.start_time && formData.end_date && 
               formData.end_time && formData.location && formData.country;
      case 'tickets': {
        if (!enableTicketSales) return true;
        if (ticketSalesMode === 'external') {
          // External mode only needs a valid URL
          return !!(externalTicketUrl && externalTicketUrl.trim().length > 0);
        }
        // Platform mode needs tiers, dates, organiser info, and terms
        const hasValidTiers = ticketTiers.length > 0 && ticketTiers.every(t =>
          t.name && t.price && parseFloat(t.price) > 0 && t.quantity && parseInt(t.quantity) > 0
        );
        const hasValidSalesDates = !!(salesStartDate && salesEndDate);
        const hasOrganiserInfo = !!(organiserBusinessName && organiserAddress && organiserPhone && organiserEmail);
        return hasValidTiers && hasValidSalesDates && hasOrganiserInfo && organiserAgreedToTerms;
      }
      case 'media':
        return true;
      case 'community':
        return true; // Community step is optional
    }
  };

  const handleNext = () => {
    if (!isStepValid()) {
      if (currentStep === 'basics' && !selectedVibe) {
        setError('Please select a vibe for your event');
      } else {
        setError(t('forms.common.fillRequiredFields'));
      }
      return;
    }
    
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id);
    } else {
      handleSubmit();
    }
    setError('');
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(s => s.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
    }
    setError('');
  };

  const handleSubmit = async () => {
    if (!isStepValid()) return;

    setLoading(true);
    setError('');

    const startDateTime = new Date(`${formData.start_date}T${formData.start_time}`);
    const endDateTime = new Date(`${formData.end_date}T${formData.end_time}`);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      let imageUrls: string[] = [];
      
      if (images.length > 0) {
        const uploadedImageUrls = await Promise.all(
        images.map(async (file) => {
          const { data, error } = await supabase.storage
            .from('event-images')
            .upload(`${user.id}/${Date.now()}-${file.name}`, file);
          
          if (error) throw error;
          
          const { data: { publicUrl } } = supabase.storage
            .from('event-images')
            .getPublicUrl(data.path);
          
          return publicUrl;
        })
        );
        imageUrls = [...uploadedImageUrls];
      } else if (existingImageUrls.length > 0) {
        imageUrls = existingImageUrls;
      }

      // Compute lowest tier price for backward compatibility
      const lowestTierPrice = enableTicketSales && ticketTiers.length > 0
        ? Math.min(...ticketTiers.map(t => parseFloat(t.price) || 0))
        : 0;

      const eventData: Record<string, any> = {
        user_id: user.id,
        title: formData.title,
        description: formData.description,
        start_date: startDateTime.toISOString(),
        end_date: endDateTime.toISOString(),
        location: formData.location,
        country: formData.country,
        latitude: formData.latitude,
        longitude: formData.longitude,
        price: enableTicketSales && ticketSalesMode === 'external' && externalTicketPrice
          ? parseFloat(externalTicketPrice) || 0
          : lowestTierPrice,
        currency: currency,
        ticket_url: enableTicketSales && ticketSalesMode === 'external' ? externalTicketUrl.trim() : null,
        image_url: imageUrls[0],
        images: imageUrls,
        community_id: formData.community_id || null,
        visibility: formData.visibility,
        // Vibe fields
        event_type: selectedVibe || null,
        primary_vibe: selectedVibe || null,
        sub_category_id: selectedSubCategory || null,
        vibe_tags: selectedTags.length > 0 ? selectedTags : [],
        suitable_for_kids: suitableForKids,
        ticket_tiers: enableTicketSales && ticketSalesMode === 'platform' ? ticketTiers.map(t => ({
          name: t.name,
          price: parseFloat(t.price),
          quantity: parseInt(t.quantity),
          description: t.description
        })) : null,
        total_capacity: enableTicketSales && ticketSalesMode === 'platform' ? totalCapacity : null,
        max_participants: !enableTicketSales && freeEventCapacity ? parseInt(freeEventCapacity) : null,
        sales_start_date: enableTicketSales && ticketSalesMode === 'platform' && salesStartDate
          ? new Date(`${salesStartDate}T${salesStartTime}`).toISOString() : null,
        sales_end_date: enableTicketSales && ticketSalesMode === 'platform' && salesEndDate
          ? new Date(`${salesEndDate}T${salesEndTime}`).toISOString() : null,
        vat_percentage: enableTicketSales && ticketSalesMode === 'platform' && vatPercentage
          ? parseFloat(vatPercentage) : null,
        organiser_business_name: enableTicketSales && ticketSalesMode === 'platform' ? organiserBusinessName || null : null,
        organiser_address: enableTicketSales && ticketSalesMode === 'platform' ? organiserAddress || null : null,
        organiser_phone: enableTicketSales && ticketSalesMode === 'platform' && organiserPhone ? `${organiserPhoneCode}${organiserPhone}` : null,
        organiser_email: enableTicketSales && ticketSalesMode === 'platform' ? organiserEmail || null : null,
        organiser_tax_number: enableTicketSales && ticketSalesMode === 'platform' ? organiserTaxNumber || null : null
      };

      let error;
      
      if (editId) {
        const { error: updateError } = await supabase
          .from('events')
          .update(eventData)
          .eq('id', editId);
        
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from('events')
          .insert([eventData]);
        
        error = insertError;
      }

      if (error) throw error;
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // --- Ticket tier helpers ---
  const getCurrencySymbol = (curr: string) => {
    switch (curr) {
      case 'GBP': return '£';
      case 'USD': return '$';
      case 'EUR': return '€';
      default: return curr + ' ';
    }
  };

  const calculateFees = (ticketPrice: number) => {
    const serviceFee = 0.50;
    const commission = ticketPrice * 0.02;
    const stripeFee = ticketPrice * 0.015 + 0.25;
    const totalFees = serviceFee + commission + stripeFee;
    const organiserReceives = ticketPrice - totalFees;
    return { serviceFee, commission, stripeFee, totalFees, organiserReceives };
  };

  const totalCapacity = ticketTiers.reduce((sum, tier) => sum + (parseInt(tier.quantity) || 0), 0);
  const sym = getCurrencySymbol(currency);

  const updateTier = (index: number, field: string, value: string) => {
    setTicketTiers(prev => prev.map((tier, i) =>
      i === index ? { ...tier, [field]: value } : tier
    ));
  };

  const addTier = () => {
    setTicketTiers(prev => [...prev, { name: '', price: '', quantity: '', description: '' }]);
  };

  const removeTier = (index: number) => {
    setTicketTiers(prev => prev.filter((_, i) => i !== index));
  };

  // Sales period constraints (max 60 days before event)
  const todayStr = new Date().toISOString().split('T')[0];
  let minSalesStart = todayStr;
  const maxSalesEnd = formData.start_date || '';
  if (formData.start_date) {
    const eventDate = new Date(formData.start_date);
    const sixtyBefore = new Date(eventDate);
    sixtyBefore.setDate(sixtyBefore.getDate() - 60);
    const sixtyBeforeStr = sixtyBefore.toISOString().split('T')[0];
    minSalesStart = sixtyBeforeStr > todayStr ? sixtyBeforeStr : todayStr;
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 'basics':
        return (
          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">{t('forms.event.eventTitle')}</Label>
              <Input
                id="title"
                type="text"
                required
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder={t('forms.event.enterEventTitle')}
              />
            </div>

            {/* Vibe Selection */}
            <div className="space-y-3">
              <Label>What's the vibe?</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(vibes.length > 0 ? vibes : DEFAULT_VIBES).map((vibe) => {
                  const isSelected = selectedVibe === vibe.id;
                  const color = VIBE_COLORS[vibe.id] || vibe.color || '#6B7280';
                  return (
                    <button
                      key={vibe.id}
                      type="button"
                      onClick={() => {
                        setSelectedVibe(vibe.id);
                        setSelectedSubCategory('');
                        setSelectedTags([]);
                      }}
                      className={`p-3 rounded-xl text-left transition-all ${
                        isSelected
                          ? 'ring-2 ring-offset-2'
                          : 'bg-gray-50 hover:bg-gray-100'
                      }`}
                      style={isSelected ? { ringColor: color, backgroundColor: `${color}15` } : {}}
                    >
                      <div className={`text-sm font-medium ${isSelected ? '' : 'text-gray-700'}`}>
                        {vibe.name}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sub-category Selection - required after vibe */}
            {selectedVibe && subCategories.filter(sc => sc.vibe_id === selectedVibe).length > 0 && (
              <div className="space-y-2">
                <Label>Choose a category (optional)</Label>
                <div className="flex flex-wrap gap-2">
                  {subCategories
                    .filter(sc => sc.vibe_id === selectedVibe)
                    .map((sc) => {
                      const isSelected = selectedSubCategory === sc.id;
                      const color = VIBE_COLORS[sc.vibe_id] || '#6B7280';
                      return (
                        <button
                          key={sc.id}
                          type="button"
                          onClick={() => setSelectedSubCategory(sc.id)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                            isSelected
                              ? 'text-white'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                          style={isSelected ? { backgroundColor: color } : {}}
                        >
                          {sc.name}
                        </button>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Additional Tags - Dropdown for all subcategories */}
            {subCategories.length > 0 && (
              <div className="space-y-2">
                <Label>Add extra tags to show in other categories (optional, max 2)</Label>
                <Select
                  value={selectedTags[0] || ''}
                  onValueChange={(value) => {
                    if (value && !selectedTags.includes(value)) {
                      setSelectedTags(prev => prev.length < 2 ? [...prev, value] : prev);
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select tags (max 2)" />
                  </SelectTrigger>
                  <SelectContent>
                    {subCategories.map((sc) => (
                      <SelectItem
                        key={sc.id}
                        value={sc.id}
                        disabled={selectedTags.includes(sc.id)}
                      >
                        {sc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {/* Show selected tags as removable chips */}
                {selectedTags.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {selectedTags.map((tagId) => {
                      const tag = subCategories.find(sc => sc.id === tagId);
                      return tag ? (
                        <span
                          key={tagId}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-accent-text/10 text-accent-text text-xs font-medium rounded-lg"
                        >
                          {tag.name}
                          <button
                            type="button"
                            onClick={() => setSelectedTags(prev => prev.filter(id => id !== tagId))}
                            className="hover:text-accent-text/70"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Family-friendly checkbox */}
            <div className="pt-2">
              <label className="flex items-center gap-3 cursor-pointer group">
                <div className="relative">
                  <input
                    type="checkbox"
                    checked={suitableForKids}
                    onChange={(e) => setSuitableForKids(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent-text/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent-text"></div>
                </div>
                <span className="text-sm text-gray-700 group-hover:text-accent-text transition-colors">
                  Suitable for kids (ages 5+)
                </span>
              </label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t('forms.event.description')}</Label>
              <Textarea
                id="description"
                required
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('forms.event.describeEvent')}
                rows={5}
              />
            </div>
          </div>
        );

      case 'details':
        return (
          <div className="space-y-6">
            {/* Date lock notice when editing event with ticket sales */}
            {hasExistingTicketSales && editId && (
              <div className="flex items-start gap-3 p-3.5 bg-amber-50 border border-amber-100 rounded-xl">
                <Lock className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">Event dates are locked</p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    Date and time cannot be changed because this event has active ticket sales. Changing the date could affect buyers who already purchased tickets.
                  </p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">{t('forms.event.startDate')}</Label>
                <Input
                  id="start-date"
                  type="date"
                  required
                  disabled={hasExistingTicketSales && !!editId}
                  value={formData.start_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                  className={hasExistingTicketSales && editId ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="start-time">{t('forms.event.startTime')}</Label>
                <Input
                  id="start-time"
                  type="time"
                  required
                  disabled={hasExistingTicketSales && !!editId}
                  value={formData.start_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, start_time: e.target.value }))}
                  className={hasExistingTicketSales && editId ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">{t('forms.event.endDate')}</Label>
                <Input
                  id="end-date"
                  type="date"
                  required
                  disabled={hasExistingTicketSales && !!editId}
                  value={formData.end_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                  className={hasExistingTicketSales && editId ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-time">{t('forms.event.endTime')}</Label>
                <Input
                  id="end-time"
                  type="time"
                  required
                  disabled={hasExistingTicketSales && !!editId}
                  value={formData.end_time}
                  onChange={(e) => setFormData(prev => ({ ...prev, end_time: e.target.value }))}
                  className={hasExistingTicketSales && editId ? 'opacity-60 cursor-not-allowed bg-gray-50' : ''}
                />
              </div>
            </div>

            <LocationPicker
              latitude={formData.latitude}
              longitude={formData.longitude}
              onChange={(lat, lng, address, countryCode) => setFormData(prev => ({
                ...prev,
                latitude: lat,
                longitude: lng,
                location: address || prev.location,
                country: countryCode || prev.country
              }))}
              label={t('forms.event.location')}
              helperText={t('forms.event.searchAddress')}
            />
          </div>
        );

      case 'tickets':
        return (
          <div className="space-y-6">
            {/* Enable toggle */}
            <div className="flex items-center space-x-3">
              <input
                id="enable-tickets"
                type="checkbox"
                checked={enableTicketSales}
                onChange={(e) => setEnableTicketSales(e.target.checked)}
                className="rounded border-gray-300 text-accent-text focus:ring-accent-text h-5 w-5"
              />
              <Label htmlFor="enable-tickets" className="cursor-pointer font-normal text-base">
                {t('forms.event.enableTicketSales')}
              </Label>
            </div>

            {!enableTicketSales && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center shrink-0">
                    <Users className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-content">Free Event</h3>
                    <p className="text-sm text-content/60 mt-0.5">
                      This is a free event. Set a maximum number of attendees to manage capacity.
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="free-capacity">Maximum Attendees</Label>
                  <Input
                    id="free-capacity"
                    type="number"
                    min="1"
                    placeholder="e.g., 50"
                    value={freeEventCapacity}
                    onChange={(e) => setFreeEventCapacity(e.target.value)}
                  />
                  <p className="text-xs text-content/40">
                    Leave blank for unlimited attendees
                  </p>
                </div>
              </div>
            )}

            {enableTicketSales && (
              <>
                {/* Ticket Sales Mode Selector */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setTicketSalesMode('platform')}
                    className={`relative flex flex-col items-center gap-2.5 p-4 rounded-xl border-2 transition-all duration-200 ${
                      ticketSalesMode === 'platform'
                        ? 'border-accent-text bg-accent-text/5 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {ticketSalesMode === 'platform' && (
                      <div className="absolute top-2.5 right-2.5">
                        <CheckCircle2 className="w-4 h-4 text-accent-text" />
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      ticketSalesMode === 'platform' ? 'bg-accent-text/10' : 'bg-gray-100'
                    }`}>
                      <CreditCard className={`w-5 h-5 ${ticketSalesMode === 'platform' ? 'text-accent-text' : 'text-gray-400'}`} />
                    </div>
                    <div className="text-center">
                      <p className={`text-sm font-semibold ${ticketSalesMode === 'platform' ? 'text-content' : 'text-content/60'}`}>
                        Sell on our platform
                      </p>
                      <p className="text-[11px] text-content/40 mt-0.5 leading-tight">
                        QR tickets, stock management & payouts — minimal fees compared to other platforms!
                      </p>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setTicketSalesMode('external')}
                    className={`relative flex flex-col items-center gap-2.5 p-4 rounded-xl border-2 transition-all duration-200 ${
                      ticketSalesMode === 'external'
                        ? 'border-accent-text bg-accent-text/5 shadow-sm'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    {ticketSalesMode === 'external' && (
                      <div className="absolute top-2.5 right-2.5">
                        <CheckCircle2 className="w-4 h-4 text-accent-text" />
                      </div>
                    )}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      ticketSalesMode === 'external' ? 'bg-accent-text/10' : 'bg-gray-100'
                    }`}>
                      <Link2 className={`w-5 h-5 ${ticketSalesMode === 'external' ? 'text-accent-text' : 'text-gray-400'}`} />
                    </div>
                    <div className="text-center">
                      <p className={`text-sm font-semibold ${ticketSalesMode === 'external' ? 'text-content' : 'text-content/60'}`}>
                        External link
                      </p>
                      <p className="text-[11px] text-content/40 mt-0.5 leading-tight">
                        Link to Eventbrite, Ticketmaster, etc.
                      </p>
                    </div>
                  </button>
                </div>

                {/* ── EXTERNAL LINK MODE ── */}
                {ticketSalesMode === 'external' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Ticket purchase link</Label>
                      <div className="relative">
                        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-content/30" />
                        <Input
                          value={externalTicketUrl}
                          onChange={(e) => setExternalTicketUrl(e.target.value)}
                          placeholder="https://www.eventbrite.com/e/your-event"
                          className="pl-9"
                        />
                      </div>
                      <p className="text-xs text-content/40">
                        Paste the full URL where attendees can purchase tickets.
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Ticket price <span className="text-content/40 font-normal">(optional)</span></Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={externalTicketPrice}
                          onChange={(e) => setExternalTicketPrice(e.target.value)}
                          placeholder="0.00"
                        />
                        <p className="text-xs text-content/40">
                          Shown on the event page for reference.
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>{t('forms.event.currency')}</Label>
                        <select
                          value={currency}
                          onChange={(e) => setCurrency(e.target.value)}
                          className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-background text-content focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 text-base"
                        >
                          <option value="EUR">€ EUR</option>
                          <option value="GBP">£ GBP</option>
                          <option value="USD">$ USD</option>
                          <option value="CHF">CHF</option>
                        </select>
                      </div>
                    </div>

                    <div className="flex items-start gap-2.5 p-3.5 bg-blue-50 border border-blue-100 rounded-xl">
                      <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                      <p className="text-xs text-blue-700 leading-relaxed">
                        Attendees will be redirected to this link when they click "Buy Tickets". No fees are charged by our platform for external ticket links.
                      </p>
                    </div>
                  </div>
                )}

                {/* ── PLATFORM MODE ── */}
                {ticketSalesMode === 'platform' && (
                <>
                {/* Stripe Connect Status Banner */}
                {stripeConnectStatus === 'active' ? (
                  <div className="flex items-center gap-3 p-3.5 bg-green-50 border border-green-100 rounded-xl">
                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-green-800">Stripe account connected</p>
                      <p className="text-xs text-green-600 mt-0.5">You're ready to receive ticket payments.</p>
                    </div>
                  </div>
                ) : stripeConnectStatus === 'under_review' ? (
                  <div className="flex items-center gap-3 p-3.5 bg-blue-50 border border-blue-100 rounded-xl">
                    <Loader2 className="h-5 w-5 text-blue-600 flex-shrink-0 animate-spin" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Account under review</p>
                      <p className="text-xs text-blue-600 mt-0.5">Stripe is verifying your account. This usually takes a few minutes. You can still create your event.</p>
                    </div>
                  </div>
                ) : stripeConnectStatus === 'pending' ? (
                  <div className="flex items-start gap-3 p-3.5 bg-amber-50 border border-amber-100 rounded-xl">
                    <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">Stripe setup incomplete</p>
                      <p className="text-xs text-amber-600 mt-0.5">Complete your Stripe onboarding to receive payments from ticket sales.</p>
                      <button
                        type="button"
                        onClick={handleSetupStripeConnect}
                        disabled={stripeConnectLoading}
                        className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
                      >
                        {stripeConnectLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                        Continue Setup
                      </button>
                    </div>
                  </div>
                ) : stripeConnectStatus !== null && stripeConnectStatus !== 'active' ? (
                  <div className="flex items-start gap-3 p-3.5 bg-blue-50 border border-blue-100 rounded-xl">
                    <CreditCard className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-blue-800">Connect Stripe to receive payments</p>
                      <p className="text-xs text-blue-600 mt-0.5">You need a Stripe account to receive money from ticket sales. It takes about 2 minutes to set up.</p>
                      <button
                        type="button"
                        onClick={handleSetupStripeConnect}
                        disabled={stripeConnectLoading}
                        className="mt-2 inline-flex items-center gap-1.5 px-3.5 py-1.5 text-xs font-medium text-white bg-accent-text rounded-lg hover:bg-accent-text/90 disabled:opacity-50 transition-colors"
                      >
                        {stripeConnectLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CreditCard className="h-3.5 w-3.5" />}
                        Connect Stripe Account
                      </button>
                    </div>
                  </div>
                ) : null}

                {/* Currency & VAT */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t('forms.event.currency')}</Label>
                    <select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-lg bg-background text-content focus:border-accent-text focus:ring-1 focus:ring-accent-text/20 text-base"
                    >
                      <option value="EUR">€ EUR</option>
                      <option value="GBP">£ GBP</option>
                      <option value="USD">$ USD</option>
                      <option value="CHF">CHF</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t('forms.event.vatIncluded')}</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      value={vatPercentage}
                      onChange={(e) => setVatPercentage(e.target.value)}
                      placeholder="e.g., 21"
                    />
                    <p className="text-xs text-content/40 mt-1">
                      Enter your country's VAT rate in %. E.g. 21% for NL, 20% for UK, 19% for DE. Leave empty if VAT does not apply.
                    </p>
                  </div>
                </div>

                {/* Ticket Tiers */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-base font-semibold text-content">{t('forms.event.ticketTiers')}</h3>
                    <button
                      type="button"
                      onClick={addTier}
                      className="flex items-center gap-1.5 text-sm font-medium text-accent-text hover:text-accent-text/80 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      {t('forms.event.addTier')}
                    </button>
                  </div>

                  <div className="space-y-3">
                    {ticketTiers.map((tier, index) => {
                      const price = parseFloat(tier.price) || 0;
                      const fees = price > 0 ? calculateFees(price) : null;
                      return (
                        <div key={index} className="border border-gray-200 rounded-xl p-4 space-y-3">
                          {/* Tier name + delete */}
                          <div className="flex items-start gap-3">
                            <div className="flex-1 space-y-2">
                              <Label>{t('forms.event.tierName')}</Label>
                              <Input
                                list="tier-presets"
                                value={tier.name}
                                onChange={(e) => updateTier(index, 'name', e.target.value)}
                                placeholder={t('forms.event.tierNamePlaceholder')}
                              />
                            </div>
                            {ticketTiers.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeTier(index)}
                                className="mt-8 p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {/* Price + Quantity */}
                          <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                              <Label>{t('forms.event.price', { symbol: sym })}</Label>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                value={tier.price}
                                onChange={(e) => updateTier(index, 'price', e.target.value)}
                                placeholder="0.00"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>{t('forms.event.quantity')}</Label>
                              <Input
                                type="number"
                                min="1"
                                step="1"
                                value={tier.quantity}
                                onChange={(e) => updateTier(index, 'quantity', e.target.value)}
                                placeholder={t('forms.event.quantityPlaceholder')}
                              />
                            </div>
                          </div>

                          {/* Description */}
                          <div className="space-y-2">
                            <Label>{t('forms.event.tierDescription')}</Label>
                            <Input
                              value={tier.description}
                              onChange={(e) => updateTier(index, 'description', e.target.value)}
                              placeholder={t('forms.event.tierDescriptionPlaceholder')}
                            />
                          </div>

                          {/* Fee breakdown per tier */}
                          {fees && (
                            <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                              <p className="text-xs font-medium text-content/60">
                                {t('forms.event.feeBreakdown', { symbol: sym, price: price.toFixed(2) })}
                              </p>
                              <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-content/50">
                                <span>{t('forms.event.serviceFee')} {sym}{fees.serviceFee.toFixed(2)}</span>
                                <span>·</span>
                                <span>{t('forms.event.commission')} {sym}{fees.commission.toFixed(2)}</span>
                                <span>·</span>
                                <span>{t('forms.event.stripeFee', { symbol: sym })} {sym}{fees.stripeFee.toFixed(2)}</span>
                              </div>
                              <p className="text-xs font-semibold text-content/70 pt-0.5">
                                {t('forms.event.youReceive', { symbol: sym, amount: fees.organiserReceives.toFixed(2) })}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Datalist for tier name presets */}
                  <datalist id="tier-presets">
                    <option value="Early Bird" />
                    <option value="Regular" />
                    <option value="VIP" />
                    <option value="Student" />
                    <option value="Group" />
                  </datalist>

                  {/* Total capacity */}
                  <div className="mt-4 flex items-center gap-2 text-sm">
                    <Users className="w-4 h-4 text-content/40" />
                    <span className="text-content/60">{t('forms.event.totalCapacity')}</span>
                    <span className="font-semibold text-content">{totalCapacity} {t('forms.event.ticketsLabel')}</span>
                  </div>
                </div>

                {/* Sales Period */}
                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-content">{t('forms.event.salesPeriod')}</h3>
                  <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                    <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                    <p className="text-xs text-blue-700">
                      {t('forms.event.salesPeriodInfo')}
                      {formData.start_date && (
                        <> {t('forms.event.earliestStart')} <strong>{new Date(minSalesStart).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</strong></>
                      )}
                    </p>
                  </div>

                  {!formData.start_date && (
                    <p className="text-sm text-amber-600">
                      {t('forms.event.setEventDateFirst')}
                    </p>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t('forms.event.salesStartDate')}</Label>
                      <Input
                        type="date"
                        value={salesStartDate}
                        onChange={(e) => setSalesStartDate(e.target.value)}
                        min={minSalesStart}
                        max={maxSalesEnd}
                        disabled={!formData.start_date}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('forms.event.startTime')}</Label>
                      <Input
                        type="time"
                        value={salesStartTime}
                        onChange={(e) => setSalesStartTime(e.target.value)}
                        disabled={!formData.start_date}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('forms.event.salesEndDate')}</Label>
                      <Input
                        type="date"
                        value={salesEndDate}
                        onChange={(e) => setSalesEndDate(e.target.value)}
                        min={salesStartDate || minSalesStart}
                        max={maxSalesEnd}
                        disabled={!formData.start_date}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{t('forms.event.endTime')}</Label>
                      <Input
                        type="time"
                        value={salesEndTime}
                        onChange={(e) => setSalesEndTime(e.target.value)}
                        disabled={!formData.start_date}
                      />
                    </div>
                  </div>
                </div>

                {/* Fee Overview */}
                <div className="bg-accent-text/5 border border-accent-text/20 rounded-xl p-4 space-y-2.5">
                  <h4 className="text-sm font-semibold text-content flex items-center gap-2">
                    <Info className="w-4 h-4 text-accent-text" />
                    Fee Overview
                  </h4>
                  <ul className="space-y-1.5 text-sm text-content/60">
                    <li className="flex justify-between">
                      <span>Platform service fee</span>
                      <span className="font-medium text-content/80">{sym}0.50 per ticket</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Platform commission</span>
                      <span className="font-medium text-content/80">2%</span>
                    </li>
                    <li className="flex justify-between">
                      <span>Stripe processing (card)</span>
                      <span className="font-medium text-content/80">1.5% + {sym}0.25</span>
                    </li>
                    {vatPercentage && parseFloat(vatPercentage) > 0 && (
                      <li className="flex justify-between border-t border-accent-text/10 pt-1.5">
                        <span>VAT (included in price)</span>
                        <span className="font-medium text-content/80">{vatPercentage}%</span>
                      </li>
                    )}
                  </ul>
                </div>

                {/* Organiser Details */}
                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-content">{t('forms.event.organisationDetails')}</h3>
                  <p className="text-sm text-content/50">{t('forms.event.organisationDetailsInfo')}</p>

                  <div className="space-y-2">
                    <Label>{t('forms.event.businessName')}</Label>
                    <Input
                      value={organiserBusinessName}
                      onChange={(e) => setOrganiserBusinessName(e.target.value)}
                      placeholder={t('forms.event.businessNamePlaceholder')}
                    />
                  </div>

                  <div className="space-y-2 relative" ref={addressResultsRef}>
                    <Label>{t('forms.event.businessAddress')}</Label>
                    <div className="relative">
                      <Input
                        value={organiserAddress}
                        onChange={(e) => handleAddressQueryChange(e.target.value)}
                        placeholder="Start typing to search your address..."
                      />
                      {addressSearching && (
                        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-content/30" />
                      )}
                    </div>
                    {showAddressResults && addressResults.length > 0 && (
                      <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                        {addressResults.map((result) => (
                          <button
                            key={result.place_id}
                            type="button"
                            onClick={() => selectAddressResult(result)}
                            className="w-full px-3 py-2.5 text-left text-sm hover:bg-accent-base/10 transition-colors border-b border-gray-50 last:border-0"
                          >
                            <span className="text-content/80 line-clamp-2">{result.display_name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-content/40">Search for your address and select from the suggestions</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>{t('forms.event.phoneNumber')}</Label>
                      <div className="flex gap-1.5">
                        <select
                          value={organiserPhoneCode}
                          onChange={(e) => setOrganiserPhoneCode(e.target.value)}
                          className="w-[100px] flex-shrink-0 px-2 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-accent-text/20 focus:border-accent-text"
                        >
                          {phoneCodes.map((pc) => (
                            <option key={pc.code} value={pc.code}>{pc.label}</option>
                          ))}
                        </select>
                        <Input
                          type="tel"
                          value={organiserPhone}
                          onChange={(e) => setOrganiserPhone(e.target.value.replace(/[^0-9]/g, ''))}
                          placeholder="612345678"
                          className="flex-1"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>{t('forms.event.emailAddress')}</Label>
                      <Input
                        type="email"
                        value={organiserEmail}
                        onChange={(e) => setOrganiserEmail(e.target.value)}
                        placeholder={t('forms.event.emailPlaceholder')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>{t('forms.event.taxNumber')} <span className="text-content/40 font-normal">{t('forms.event.optional')}</span></Label>
                    <Input
                      value={organiserTaxNumber}
                      onChange={(e) => setOrganiserTaxNumber(e.target.value)}
                      placeholder={t('forms.event.taxNumberPlaceholder')}
                    />
                  </div>
                </div>

                {/* Organiser Terms Agreement */}
                <label className="flex items-start gap-3 p-4 bg-amber-50/60 rounded-xl border border-amber-200/60 cursor-pointer group mt-6">
                  <input
                    type="checkbox"
                    checked={organiserAgreedToTerms}
                    onChange={(e) => setOrganiserAgreedToTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-accent-text focus:ring-accent-text/20 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-sm text-content/70 leading-relaxed">
                    I have read and agree to the{' '}
                    <a
                      href="/ticket-terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-accent-text underline hover:text-accent-text/80 font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Ticket Sale Terms & Conditions
                    </a>
                    , including the fee structure, refund policy, payout schedule (funds released 5 days after the event),
                    and that refunds may be charged back from your Stripe account if issued after payout.
                  </span>
                </label>
                </>
                )}
              </>
            )}
          </div>
        );

      case 'media':
        return (
          <div className="space-y-6">
            <FileInput
              label={t('forms.event.eventImages')}
              onChange={setImages}
              maxFiles={5}
              accept="image/*"
              multiple
              description={t('forms.event.uploadImages')}
            />
            <div className="flex flex-wrap gap-4">
              {images.map((file, index) => (
                <div key={index} className="relative">
                  <div className="aspect-w-16 aspect-h-9">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`Preview ${index + 1}`}
                      className="h-16 w-16 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setImages(prev => prev.filter((_, i) => i !== index))}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
              {images.length === 0 && existingImageUrls.map((url, index) => (
                <div key={`existing-${index}`} className="relative">
                  <img
                    src={url}
                    alt={`Existing ${index + 1}`}
                    className="h-16 w-16 object-cover rounded-lg"
                  />
                  <div className="absolute -top-2 -right-2 bg-gray-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                    ✓
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'community':
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-content mb-4">{t('forms.event.communityPosting')}</h3>
              <p className="text-sm text-content/60 mb-6">
                {t('forms.event.communityPostingInfo')}
              </p>
            </div>

            {userCommunities.length > 0 ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-2">
                    {t('forms.event.postToCommunity')}
                  </label>
                  <select
                    value={formData.community_id}
                    onChange={(e) => setFormData(prev => ({ ...prev, community_id: e.target.value }))}
                    className="w-full px-4 py-3 border border-accent-text/20 rounded-lg focus:border-accent-text focus:ring-1 focus:ring-accent-text/20"
                  >
                    <option value="">{t('forms.event.noCommunity')}</option>
                    {userCommunities.map((community) => (
                      <option key={community.id} value={community.id}>
                        {community.name}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.community_id && (
                  <div>
                    <label className="block text-sm font-medium text-content mb-2">
                      {t('forms.event.eventVisibility')}
                    </label>
                    <div className="space-y-3">
                      <label className="flex items-center p-3 border border-accent-text/20 rounded-lg cursor-pointer hover:bg-accent-base/5">
                        <input
                          type="radio"
                          name="visibility"
                          value="public"
                          checked={formData.visibility === 'public'}
                          onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value as 'public' | 'private' }))}
                          className="mr-3"
                        />
                        <div className="flex items-center">
                          <Globe className="h-4 w-4 mr-2 text-green-600" />
                          <div>
                            <div className="font-medium text-content">{t('forms.event.publicEvent')}</div>
                            <div className="text-sm text-content/60">
                              {t('forms.event.publicEventDesc')}
                            </div>
                          </div>
                        </div>
                      </label>
                      
                      <label className="flex items-center p-3 border border-accent-text/20 rounded-lg cursor-pointer hover:bg-accent-base/5">
                        <input
                          type="radio"
                          name="visibility"
                          value="private"
                          checked={formData.visibility === 'private'}
                          onChange={(e) => setFormData(prev => ({ ...prev, visibility: e.target.value as 'public' | 'private' }))}
                          className="mr-3"
                        />
                        <div className="flex items-center">
                          <Lock className="h-4 w-4 mr-2 text-gray-600" />
                          <div>
                            <div className="font-medium text-content">{t('forms.event.privateEvent')}</div>
                            <div className="text-sm text-content/60">
                              {t('forms.event.privateEventDesc')}
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <Users className="h-12 w-12 text-content/20 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-content mb-2">{t('forms.event.noCommunitiesFound')}</h3>
                <p className="text-sm text-content/60 mb-4">
                  {t('forms.event.needAdminAccess')}
                </p>
                <p className="text-sm text-content/60">
                  {t('forms.event.standaloneEvent')}
                </p>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case 'basics': return t('forms.event.basicInformation');
      case 'details': return t('forms.event.eventDetails');
      case 'tickets': return t('forms.event.ticketInformation');
      case 'media': return t('forms.event.eventMedia');
      case 'community': return t('forms.event.communityPosting');
      default: return '';
    }
  };

  const currentStepIndex = steps.findIndex(s => s.id === currentStep);

  const modalOverlayClass = "fixed inset-0 min-h-[100dvh] bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-0 lg:p-4";
  const modalInnerClass = "bg-background rounded-none lg:rounded-lg w-full h-full min-h-[100dvh] lg:min-h-0 lg:h-auto lg:max-w-2xl lg:max-h-[90vh] overflow-hidden flex flex-col lg:shadow-2xl";

  if (loading) {
    const loadingContent = (
      <div className={modalOverlayClass}>
        <div className={modalInnerClass}>
          <div className="flex-1 flex items-center justify-center">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-text"></div>
              <span className="ml-3 text-content">{t('forms.event.loadingEventData')}</span>
            </div>
          </div>
        </div>
      </div>
    );
    return typeof document !== 'undefined' ? createPortal(loadingContent, document.body) : loadingContent;
  }

  const content = (
    <div className={modalOverlayClass}>
      <div className={modalInnerClass}>
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-content">
              {getStepTitle()}
            </h2>
            <p className="text-sm text-content/60 mt-1">{t('forms.common.stepOf', { current: currentStepIndex + 1, total: steps.length })}</p>
          </div>
          <button onClick={onClose} className="text-content/60 hover:text-content">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 bg-gray-50">
          <div className="flex gap-2">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`flex-1 h-2 rounded-full transition-colors ${
                  index <= currentStepIndex ? 'bg-accent-text' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {submitted ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold text-content mb-2">Event Submitted!</h3>
              <p className="text-content/70 max-w-md mb-6">
                Your event has been submitted and is pending review. You'll be notified once it's approved and published.
              </p>
              <button
                onClick={onClose}
                className="px-6 py-2 bg-accent-text text-white rounded-full font-medium hover:bg-accent-text/90 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-md text-sm">{error}</div>
              )}

              {renderStepContent()}
            </>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
        <div className="border-t border-gray-200 p-6 flex justify-between items-center">
          <button
            type="button"
            onClick={handleBack}
            disabled={currentStep === 'basics'}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 'basics'
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-content hover:bg-gray-100'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            {t('forms.common.previous')}
          </button>

          {currentStep !== 'community' ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={!isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                isStepValid()
                  ? 'bg-accent-text hover:bg-accent-text/90'
                  : 'bg-gray-300 cursor-not-allowed'
              }`}
            >
              {t('forms.common.next')}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNext}
              disabled={loading || !isStepValid()}
              className={`flex items-center gap-2 px-6 py-2 rounded-full font-medium text-white transition-colors ${
                loading || !isStepValid()
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-accent-text hover:bg-accent-text/90'
              }`}
            >
              {loading ? (editId ? t('forms.common.updating') : t('forms.common.creating')) : (editId ? t('forms.event.updateEvent') : t('forms.event.createEvent'))}
            </button>
          )}
        </div>
        )}
      </div>
    </div>
  );
  return typeof document !== 'undefined' ? createPortal(content, document.body) : content;
}